import { useEffect, useMemo, useState } from "react";

import { Box } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";

import bankNowLogo from "assets/banks/bank-now.png";
import cembraLogo from "assets/banks/cembra.png";
import cornerLogo from "assets/banks/corner.png";
import { Container, LayoutStandard, Loader } from "components";
import { AuthorityConstants } from "constant";
import { DashboardService, TargetService } from "services";
import {
  AgentTargetOptionResponse,
  AgentKpiTargetRequest,
  AgentKpiTargetResponse,
  AgentTargetResponse,
  Bank,
  DashboardAgentResponse,
  TargetOptionResponse,
  TargetRequest,
  TargetResponse,
} from "types";
import { AppUtils } from "utils";

import AgentTargetsSection, {
  AgentTargetFormRow,
  AgentTargetMetricConfig,
} from "./AgentTargetsSection";
import TargetSection from "./TargetSection";
import classes from "./TargetsPage.module.css";

type TargetGroup = "BANK" | "KPI" | "CONVERSION" | "PROGRESS";
type TargetValues = Record<string, number | "">;
type TargetSectionModel = {
  group: string;
  options: TargetOptionResponse[];
};
type TargetGroupConfig = {
  hintKey: string;
  inputLabelKey: string;
  inputPlaceholderKey: string;
  plainInput: boolean;
  titleKey: string;
};

const TARGET_GROUP_ORDER: readonly TargetGroup[] = ["KPI", "BANK", "CONVERSION", "PROGRESS"];
const CONTROLLED_LAYOUT_GROUPS = ["KPI", "BANK", "CONVERSION", "PROGRESS"] as const;
const CONTROLLED_LAYOUT_GROUPS_SET = new Set<string>(CONTROLLED_LAYOUT_GROUPS);
const CURRENCY_TARGET_UNIT = "CHF";

const TARGET_GROUP_CONFIG: Record<TargetGroup, TargetGroupConfig> = {
  BANK: {
    hintKey: "targets.groups.BANK.hint",
    inputLabelKey: "targets.fields.targetAmount",
    inputPlaceholderKey: "targets.fields.targetPlaceholder",
    plainInput: false,
    titleKey: "targets.groups.BANK.title",
  },
  CONVERSION: {
    hintKey: "targets.groups.CONVERSION.hint",
    inputLabelKey: "targets.fields.targetValue",
    inputPlaceholderKey: "targets.fields.targetValuePlaceholder",
    plainInput: true,
    titleKey: "targets.groups.CONVERSION.title",
  },
  KPI: {
    hintKey: "targets.groups.KPI.hint",
    inputLabelKey: "targets.fields.targetCount",
    inputPlaceholderKey: "targets.fields.targetCountPlaceholder",
    plainInput: true,
    titleKey: "targets.groups.KPI.title",
  },
  PROGRESS: {
    hintKey: "targets.groups.PROGRESS.hint",
    inputLabelKey: "targets.fields.targetAmount",
    inputPlaceholderKey: "targets.fields.targetPlaceholder",
    plainInput: false,
    titleKey: "targets.groups.PROGRESS.title",
  },
};

const TARGET_TYPE_LABEL_KEYS: Record<string, string> = {
  APPROVED: "targets.types.APPROVED",
  APPROVED_TO_DISBURSED_RATE: "targets.types.APPROVED_TO_DISBURSED_RATE",
  BANK_LEAD: "targets.types.BANK_LEAD",
  DISBURSED: "targets.types.DISBURSED",
  LEAD_TO_APPROVED_RATE: "targets.types.LEAD_TO_APPROVED_RATE",
  MONTHLY_PROGRESS: "targets.types.MONTHLY_PROGRESS",
  QUALIFICATION: "targets.types.QUALIFICATION",
  SMALL_WIN: "targets.types.SMALL_WIN",
  WEB_APP_REQUEST: "targets.types.WEB_APP_REQUEST",
  YEARLY_PROGRESS: "targets.types.YEARLY_PROGRESS",
};

const AGENT_TARGET_METRIC_LABEL_KEYS: Record<string, string> = {
  approved: "dashboard.kpis.metrics.approved",
  disbursedCount: "dashboard.kpis.metrics.disbursed",
  disbursedSum: "targets.fields.targetPayoutAmount",
  leadsInBank: "dashboard.admin.operative.metrics.leadsInBank",
  qualifications: "dashboard.kpis.metrics.qualifications",
};

const BANK_LOGOS: Partial<Record<Bank, string>> = {
  BANK_NOW: bankNowLogo,
  CEMBRA_MONEY_AG: cembraLogo,
  CORNER: cornerLogo,
};

const buildInitialTargetValues = (options: TargetOptionResponse[]): TargetValues =>
  Object.fromEntries(options.map(({ type }) => [type, ""])) as TargetValues;

const mapTargetsResponseToFormValues = (
  options: TargetOptionResponse[],
  items: TargetResponse[],
): TargetValues => {
  const nextValues = buildInitialTargetValues(options);

  items.forEach((item) => {
    if (item.type in nextValues) {
      nextValues[item.type] = item.targetAmount;
    }
  });

  return nextValues;
};

const mergeSavedPayloads = (values: TargetValues, payloads: TargetRequest[]): TargetValues =>
  payloads.reduce<TargetValues>(
    (acc, payload) => ({
      ...acc,
      [payload.type]: payload.targetAmount,
    }),
    { ...values },
  );

const getTargetGroupOrder = (group: string) => {
  const index = TARGET_GROUP_ORDER.indexOf(group as TargetGroup);
  return index === -1 ? TARGET_GROUP_ORDER.length : index;
};

const formatTargetFallbackLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => AppUtils.capitalize(part))
    .join(" ");

const getAgentTargetMetricLabel = (
  metricOption: AgentTargetOptionResponse,
  t: ReturnType<typeof useTranslation>["t"],
) => {
  const fallbackLabel = formatTargetFallbackLabel(metricOption.metric);
  const labelKey = AGENT_TARGET_METRIC_LABEL_KEYS[metricOption.metric];

  return labelKey
    ? t(labelKey, { defaultValue: fallbackLabel })
    : fallbackLabel;
};

const compactSections = (items: Array<TargetSectionModel | undefined>) =>
  items.filter((item): item is TargetSectionModel => Boolean(item));

const buildInitialAgentTargetValues = (metricOptions: AgentTargetOptionResponse[]) =>
  Object.fromEntries(metricOptions.map(({ metric }) => [metric, ""])) as Record<
    string,
    number | ""
  >;

const mapAgentTargetResponseToFormValues = (
  metricOptions: AgentTargetOptionResponse[],
  response?: AgentTargetResponse,
) => {
  const knownMetricKeys = new Set(metricOptions.map(({ metric }) => metric));
  const targets = buildInitialAgentTargetValues(metricOptions);
  const extraTargets: AgentKpiTargetResponse[] = [];

  response?.targets?.forEach((item) => {
    if (knownMetricKeys.has(item.metric)) {
      targets[item.metric] = item.value;
      return;
    }

    extraTargets.push(item);
  });

  return {
    extraTargets,
    targets,
  };
};

const mapAgentTargetResponseToFormRow = (
  agent: Pick<AgentTargetFormRow, "agentId" | "agentName">,
  metricOptions: AgentTargetOptionResponse[],
  response?: AgentTargetResponse,
): AgentTargetFormRow => ({
  agentId: agent.agentId,
  agentName: agent.agentName,
  ...mapAgentTargetResponseToFormValues(metricOptions, response),
});

const mapDashboardAgentsToFormValues = (
  items: DashboardAgentResponse[],
  metricOptions: AgentTargetOptionResponse[],
  targetsByAgentId: Map<number, AgentTargetResponse> = new Map(),
): AgentTargetFormRow[] =>
  items.map((item) =>
    mapAgentTargetResponseToFormRow(
      {
        agentId: item.agentId,
        agentName: item.name,
      },
      metricOptions,
      targetsByAgentId.get(item.agentId),
    ),
  );

const mapAgentTargetToRequest = (
  agent: AgentTargetFormRow,
  metricOptions: AgentTargetOptionResponse[],
): AgentKpiTargetRequest[] => {
  const knownMetricKeys = new Set(metricOptions.map(({ metric }) => metric));

  return [
    ...metricOptions.flatMap(({ metric }) => {
      const value = agent.targets[metric];

      return value === "" ? [] : [{ metric, value: Number(value) }];
    }),
    ...(agent.extraTargets ?? [])
      .filter(({ metric }) => !knownMetricKeys.has(metric))
      .map(({ metric, value }) => ({ metric, value })),
  ];
};

const TargetsPage = () => {
  const { t } = useTranslation();
  const isSingleColumnLayout = useMediaQuery("(max-width: 920px)");
  const isTwoColumnLayout = useMediaQuery("(max-width: 1480px)");
  const [agentTargets, setAgentTargets] = useState<AgentTargetFormRow[]>([]);
  const [hasLoadedAgentTargets, setHasLoadedAgentTargets] = useState(false);
  const [hasLoadedTargetOptions, setHasLoadedTargetOptions] = useState(false);
  const [agentTargetOptions, setAgentTargetOptions] = useState<AgentTargetOptionResponse[]>([]);
  const [savedAgentTargets, setSavedAgentTargets] = useState<AgentTargetFormRow[]>([]);
  const [targetOptions, setTargetOptions] = useState<TargetOptionResponse[]>([]);
  const [targets, setTargets] = useState<TargetValues>({});
  const [savedTargets, setSavedTargets] = useState<TargetValues>({});
  const [isLoadingTargets, setIsLoadingTargets] = useState(true);
  const [isSavingAgentTargets, setIsSavingAgentTargets] = useState(false);
  const [savingGroups, setSavingGroups] = useState<Record<string, boolean>>({});

  const orderedTargetOptions = useMemo(
    () =>
      [...targetOptions].sort(
        (left, right) => getTargetGroupOrder(left.group) - getTargetGroupOrder(right.group),
      ),
    [targetOptions],
  );
  const groupedTargetOptions = useMemo(
    () =>
      orderedTargetOptions.reduce<Record<string, TargetOptionResponse[]>>((acc, option) => {
        if (!acc[option.group]) {
          acc[option.group] = [];
        }

        acc[option.group].push(option);
        return acc;
      }, {}),
    [orderedTargetOptions],
  );
  const sections = useMemo<TargetSectionModel[]>(() => {
    const knownSections = TARGET_GROUP_ORDER.map((group) => ({
      group,
      options: groupedTargetOptions[group] ?? [],
    })).filter(({ options }) => options.length > 0);

    const unknownSections = Object.entries(groupedTargetOptions)
      .filter(([group]) => !TARGET_GROUP_ORDER.includes(group as TargetGroup))
      .map(([group, options]) => ({ group, options }));

    return [...knownSections, ...unknownSections];
  }, [groupedTargetOptions]);
  const sectionsByGroup = useMemo(
    () =>
      sections.reduce<Record<string, TargetSectionModel>>((acc, section) => {
        acc[section.group] = section;
        return acc;
      }, {}),
    [sections],
  );
  const extraSections = useMemo(
    () => sections.filter(({ group }) => !CONTROLLED_LAYOUT_GROUPS_SET.has(group)),
    [sections],
  );
  const optionsByType = useMemo(
    () =>
      orderedTargetOptions.reduce<Record<string, TargetOptionResponse>>((acc, option) => {
        acc[option.type] = option;
        return acc;
      }, {}),
    [orderedTargetOptions],
  );
  const agentTargetMetrics = useMemo<AgentTargetMetricConfig[]>(
    () =>
      agentTargetOptions.map((metricOption) => ({
        label: getAgentTargetMetricLabel(metricOption, t),
        metric: metricOption.metric,
        placeholder: t(
          metricOption.unit === CURRENCY_TARGET_UNIT
            ? "targets.fields.targetPlaceholder"
            : "targets.fields.targetCountPlaceholder",
        ),
        plainInput: metricOption.unit !== CURRENCY_TARGET_UNIT,
      })),
    [agentTargetOptions, t],
  );

  const getTargetLabel = (option: TargetOptionResponse) => {
    const fallbackLabel = formatTargetFallbackLabel(option.type);

    if (option.group === "BANK") {
      return t(`bankSelection.bank.${option.type}`, { defaultValue: fallbackLabel });
    }

    const labelKey = TARGET_TYPE_LABEL_KEYS[option.type];
    return labelKey ? t(labelKey, { defaultValue: fallbackLabel }) : fallbackLabel;
  };

  const getTargetLabelByType = (type: string) => {
    const option = optionsByType[type];
    return option ? getTargetLabel(option) : formatTargetFallbackLabel(type);
  };

  const getTargetHint = (group: string) => {
    const groupConfig = TARGET_GROUP_CONFIG[group as TargetGroup];
    return groupConfig ? t(groupConfig.hintKey) : t("targets.hints.default");
  };

  const getTargetLogo = (option: TargetOptionResponse) =>
    option.group === "BANK" ? BANK_LOGOS[option.type as Bank] : undefined;

  const getSectionTitle = (group: string) => {
    const groupConfig = TARGET_GROUP_CONFIG[group as TargetGroup];
    return groupConfig ? t(groupConfig.titleKey) : formatTargetFallbackLabel(group);
  };
  const getSectionInputConfig = (group: string) => {
    const groupConfig = TARGET_GROUP_CONFIG[group as TargetGroup];

    return {
      inputLabel: t(groupConfig?.inputLabelKey ?? "targets.fields.targetValue"),
      inputPlaceholder: t(
        groupConfig?.inputPlaceholderKey ?? "targets.fields.targetValuePlaceholder",
      ),
      plainInput: groupConfig?.plainInput ?? true,
    };
  };

  const isSectionSaving = (group: string) => Boolean(savingGroups[group]);
  const hasSectionUnsavedChanges = (options: TargetOptionResponse[]) =>
    options.some(({ type }) => targets[type] !== savedTargets[type]);
  const isSectionComplete = (options: TargetOptionResponse[]) =>
    options.every(({ type }) => targets[type] !== "");
  const getChangedPayloads = (options: TargetOptionResponse[]): TargetRequest[] =>
    options
      .filter(({ type }) => targets[type] !== savedTargets[type])
      .map(({ type }) => ({
        targetAmount: Number(targets[type]),
        type,
      }));

  const handleTargetChange = (type: string, value: string | number) => {
    setTargets((prev) => ({
      ...prev,
      [type]: value === "" ? "" : Number(value),
    }));
  };

  const handleAgentTargetChange = (
    agentId: number,
    metric: string,
    value: string | number,
  ) => {
    setAgentTargets((prev) =>
      prev.map((agent) =>
        agent.agentId === agentId
          ? {
              ...agent,
              targets: {
                ...agent.targets,
                [metric]: value === "" ? "" : Number(value),
              },
            }
          : agent,
      ),
    );
  };

  const getSavedAgentTarget = (agentId: number) =>
    savedAgentTargets.find((item) => item.agentId === agentId);

  const hasAgentTargetChanges = (agent: AgentTargetFormRow) => {
    const savedAgent = getSavedAgentTarget(agent.agentId);

    return agentTargetOptions.some(
      ({ metric }) => agent.targets[metric] !== (savedAgent?.targets[metric] ?? ""),
    );
  };
  const hasAgentSectionUnsavedChanges = () => agentTargets.some(hasAgentTargetChanges);
  const getChangedAgentTargets = () => agentTargets.filter(hasAgentTargetChanges);

  useEffect(() => {
    let mounted = true;

    const loadTargets = async () => {
      try {
        const [optionsData, targetsData] = await Promise.all([
          TargetService.getTargetOptions(),
          TargetService.getTargets(),
        ]);

        if (!mounted) {
          return;
        }

        const nextOptions = optionsData.global;
        const nextAgentOptions = optionsData.agent;
        const nextValues = mapTargetsResponseToFormValues(nextOptions, targetsData ?? []);
        setTargetOptions(nextOptions);
        setAgentTargetOptions(nextAgentOptions);
        setTargets(nextValues);
        setSavedTargets(nextValues);
      } catch {
        if (!mounted) {
          return;
        }

        notifications.show({
          color: "red",
          message: t("targets.notifications.loadErrorMessage"),
          title: t("targets.notifications.loadErrorTitle"),
        });
      } finally {
        if (mounted) {
          setIsLoadingTargets(false);
          setHasLoadedTargetOptions(true);
        }
      }
    };

    loadTargets();

    return () => {
      mounted = false;
    };
  }, [t]);

  useEffect(() => {
    if (!hasLoadedTargetOptions) {
      return undefined;
    }

    let mounted = true;

    const loadAgentTargets = async () => {
      try {
        const data = await DashboardService.getAgents();

        if (!mounted) {
          return;
        }

        const agentItems = data ?? [];
        const targetResults = await Promise.allSettled(
          agentItems.map((agent) => TargetService.getAgentTarget(agent.agentId)),
        );

        if (!mounted) {
          return;
        }

        const targetsByAgentId = new Map(
          targetResults.flatMap((result, index) =>
            result.status === "fulfilled" && result.value
              ? [[agentItems[index].agentId, result.value] as const]
              : [],
          ),
        );
        const nextAgentTargets = mapDashboardAgentsToFormValues(
          agentItems,
          agentTargetOptions,
          targetsByAgentId,
        );
        setAgentTargets(nextAgentTargets);
        setSavedAgentTargets(nextAgentTargets);
      } catch {
        if (!mounted) {
          return;
        }

        setAgentTargets([]);
        setSavedAgentTargets([]);
      } finally {
        if (mounted) {
          setHasLoadedAgentTargets(true);
        }
      }
    };

    loadAgentTargets();

    return () => {
      mounted = false;
    };
  }, [agentTargetOptions, hasLoadedTargetOptions]);

  const handleSaveSection = async (group: string, options: TargetOptionResponse[]) => {
    if (isSectionSaving(group)) {
      return;
    }

    if (!isSectionComplete(options)) {
      notifications.show({
        color: "red",
        message: t("targets.notifications.validationMessage"),
        title: t("targets.notifications.validationTitle"),
      });
      return;
    }

    const payloads = getChangedPayloads(options);

    if (payloads.length === 0) {
      return;
    }

    setSavingGroups((prev) => ({
      ...prev,
      [group]: true,
    }));

    const results = await Promise.allSettled(payloads.map((payload) => TargetService.saveTarget(payload)));
    const successfulPayloads = payloads.filter((_, index) => results[index].status === "fulfilled");
    const failedTargets = results.flatMap((result, index) =>
      result.status === "rejected" ? [getTargetLabelByType(payloads[index].type)] : [],
    );

    setSavedTargets((prev) => mergeSavedPayloads(prev, successfulPayloads));

    notifications.show(
      failedTargets.length === 0
        ? {
            color: "teal",
            message: t("targets.notifications.saveSuccessMessage"),
            title: t("targets.notifications.saveSuccessTitle"),
          }
        : {
            color: "red",
            message:
              failedTargets.length === payloads.length
                ? t("targets.notifications.saveErrorMessage")
                : t("targets.notifications.savePartialErrorMessage", {
                    items: failedTargets.join(", "),
                  }),
            title: t("targets.notifications.saveErrorTitle"),
          },
    );

    setSavingGroups((prev) => ({
      ...prev,
      [group]: false,
    }));
  };

  const handleSaveAgentTargets = async () => {
    if (isSavingAgentTargets) {
      return;
    }

    const changedAgentTargets = getChangedAgentTargets();

    if (changedAgentTargets.length === 0) {
      return;
    }

    setIsSavingAgentTargets(true);

    const results = await Promise.allSettled(
      changedAgentTargets.map((agent) =>
        TargetService.saveAgentTarget(agent.agentId, {
          targets: mapAgentTargetToRequest(agent, agentTargetOptions),
        }),
      ),
    );
    const successfulAgentTargets = new Map(
      results.flatMap((result, index) =>
        result.status === "fulfilled"
          ? [
              [
                changedAgentTargets[index].agentId,
                mapAgentTargetResponseToFormRow(
                  changedAgentTargets[index],
                  agentTargetOptions,
                  result.value,
                ),
              ] as const,
            ]
          : [],
      ),
    );
    const failedAgents = results.flatMap((result, index) =>
      result.status === "rejected" ? [changedAgentTargets[index].agentName] : [],
    );

    const mergeSuccessfulAgentTargets = (items: AgentTargetFormRow[]) =>
      items.map((agent) => successfulAgentTargets.get(agent.agentId) ?? agent);

    setAgentTargets(mergeSuccessfulAgentTargets);
    setSavedAgentTargets(mergeSuccessfulAgentTargets);

    notifications.show(
      failedAgents.length === 0
        ? {
            color: "teal",
            message: t("targets.notifications.saveSuccessMessage"),
            title: t("targets.notifications.saveSuccessTitle"),
          }
        : {
            color: "red",
            message:
              failedAgents.length === changedAgentTargets.length
                ? t("targets.notifications.saveErrorMessage")
                : t("targets.notifications.savePartialErrorMessage", {
                    items: failedAgents.join(", "),
                  }),
            title: t("targets.notifications.saveErrorTitle"),
          },
    );

    setIsSavingAgentTargets(false);
  };

  const layoutColumns = useMemo(() => {
    if (isSingleColumnLayout) {
      return [
        compactSections([sectionsByGroup.KPI]),
        compactSections([sectionsByGroup.BANK]),
        compactSections([sectionsByGroup.CONVERSION]),
        compactSections([sectionsByGroup.PROGRESS]),
      ];
    }

    if (isTwoColumnLayout) {
      return [
        compactSections([sectionsByGroup.KPI, sectionsByGroup.PROGRESS]),
        compactSections([sectionsByGroup.BANK, sectionsByGroup.CONVERSION]),
      ];
    }

    return [
      compactSections([sectionsByGroup.KPI]),
      compactSections([sectionsByGroup.BANK, sectionsByGroup.CONVERSION]),
      compactSections([sectionsByGroup.PROGRESS]),
    ];
  }, [isSingleColumnLayout, isTwoColumnLayout, sectionsByGroup]);

  const gridClassName = isSingleColumnLayout
    ? classes.gridSingle
    : isTwoColumnLayout
      ? classes.gridDouble
      : classes.gridTriple;

  const renderSection = ({ group, options }: TargetSectionModel) => {
    const { inputLabel, inputPlaceholder, plainInput } = getSectionInputConfig(group);

    return (
      <div key={group} className={classes.sectionItem}>
        <TargetSection
          title={getSectionTitle(group)}
          options={options}
          values={targets}
          getTargetLabel={getTargetLabel}
          getTargetHint={getTargetHint}
          getTargetLogo={getTargetLogo}
          isDisabled={
            isLoadingTargets ||
            isSectionSaving(group) ||
            !hasSectionUnsavedChanges(options) ||
            !isSectionComplete(options)
          }
          isSaving={isSectionSaving(group)}
          inputLabel={inputLabel}
          inputPlaceholder={inputPlaceholder}
          plainInput={plainInput}
          onSave={() => handleSaveSection(group, options)}
          onTargetChange={handleTargetChange}
          saveLabel={t("action.save")}
        />
      </div>
    );
  };

  return (
    <LayoutStandard
      title={AppUtils.composeTabTitle(t("header.targets"))}
      authority={AuthorityConstants.ROLE_TARGET}
    >
      <Container>
        <div className={classes.page}>
          <Box pos="relative">
            <Loader overlay={true} visible={isLoadingTargets} />
            <div className={`${classes.grid} ${gridClassName}`}>
              {layoutColumns.map((column, index) => (
                <div key={index} className={classes.column}>
                  {column.map(renderSection)}
                </div>
              ))}
            </div>

            {extraSections.length > 0 ? (
              <div className={classes.extraSections}>{extraSections.map(renderSection)}</div>
            ) : null}

            <div className={`${classes.extraSections} ${classes.agentSection}`}>
              <AgentTargetsSection
                agents={agentTargets}
                emptyLabel={t("results.no")}
                hint={t("targets.groups.AGENT.hint")}
                isDisabled={isSavingAgentTargets || !hasAgentSectionUnsavedChanges()}
                isSaving={isSavingAgentTargets}
                metrics={agentTargetMetrics}
                onTargetChange={handleAgentTargetChange}
                onSave={handleSaveAgentTargets}
                saveLabel={t("action.save")}
                showEmptyState={hasLoadedAgentTargets}
                title={t("targets.groups.AGENT.title")}
              />
            </div>
          </Box>
        </div>
      </Container>
    </LayoutStandard>
  );
};

export default TargetsPage;
